import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  listCertificates, deleteCertificate,
} from '@/api/certificates';
import type { CertificateListItem, CertificateType } from '@/api/certificates';

type Tab = '' | CertificateType;

const TABS: { value: Tab; label: string }[] = [
  { value: '',             label: 'All'          },
  { value: 'Baptism',      label: '✦ Baptism'    },
  { value: 'Confirmation', label: '✦ Confirmation'},
  { value: 'Marriage',     label: '✦ Marriage'   },
  { value: 'Burial',       label: '✦ Burial'     },
];

const TYPE_BADGE: Record<CertificateType, string> = {
  Baptism:      'bg-blue-100  text-blue-800',
  Confirmation: 'bg-purple-100 text-purple-800',
  Marriage:     'bg-pink-100  text-pink-800',
  Burial:       'bg-gray-200  text-gray-700',
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function CertificatesPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [tab,        setTab]      = useState<Tab>('');
  const [yearFilter, setYear]     = useState('');
  const [rows,       setRows]     = useState<CertificateListItem[]>([]);
  const [loading,    setLoading]  = useState(false);
  const [error,      setError]    = useState('');
  const [deleting,   setDeleting] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await listCertificates({
        type:     tab || undefined,
        year:     yearFilter ? parseInt(yearFilter, 10) : undefined,
      });
      setRows(data);
    } catch {
      setError('Failed to load certificates.');
    } finally {
      setLoading(false);
    }
  }, [tab, yearFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this certificate record?')) return;
    setDeleting(id);
    try {
      await deleteCertificate(id);
      setRows((r) => r.filter((x) => x.certificateId !== id));
    } catch {
      alert('Delete failed.');
    } finally {
      setDeleting(null);
    }
  };

  // Stats
  const counts = rows.reduce((acc, r) => {
    acc[r.certificateType] = (acc[r.certificateType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-georgia font-bold text-navy">Church Certificates</h1>
          <p className="text-sm text-gray-500 font-georgia">
            Baptism · Confirmation · Marriage · Burial records
          </p>
        </div>
        <button
          onClick={() => navigate('/certificates/new')}
          className="px-4 py-2 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700 whitespace-nowrap"
        >
          + New Certificate
        </button>
      </div>

      {/* Stats row */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(['Baptism','Confirmation','Marriage','Burial'] as CertificateType[]).map((t) => (
            <div key={t} className="bg-white rounded-lg shadow-sm border px-3 py-2 text-center">
              <p className="text-2xl font-bold text-navy">{counts[t] || 0}</p>
              <p className="text-xs text-gray-500 font-georgia">{t}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs + Year filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-1 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`px-3 py-1.5 rounded text-sm font-georgia transition-colors ${
                tab === t.value
                  ? 'bg-teal text-white'
                  : 'bg-white border text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto">
          <select
            value={yearFilter}
            onChange={(e) => setYear(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm font-georgia"
          >
            <option value="">All years</option>
            {[2026, 2025, 2024, 2023, 2022, 2021, 2020].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
        {loading && (
          <p className="p-6 text-center text-gray-400 font-georgia text-sm">Loading…</p>
        )}
        {error && (
          <p className="p-6 text-center text-red-500 font-georgia text-sm">{error}</p>
        )}
        {!loading && !error && rows.length === 0 && (
          <p className="p-10 text-center text-gray-400 font-georgia text-sm">
            No certificate records found.
          </p>
        )}
        {!loading && rows.length > 0 && (
          <div className="hidden sm:block">
          <table className="w-full text-sm font-georgia">
            <thead className="bg-navy text-white text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Ref No</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.certificateId} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-500">{r.refSerialNo || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_BADGE[r.certificateType]}`}>
                      {r.certificateType}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-navy font-medium">{r.primaryName || '—'}</td>
                  <td className="px-3 py-2 text-gray-500">{fmtDate(r.issuedDate)}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{fmtDate(r.createdAt)}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/certificates/${r.certificateId}/edit`)}
                      className="text-teal hover:underline text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => navigate(`/certificates/${r.certificateId}/print`)}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      Print
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(r.certificateId)}
                        disabled={deleting === r.certificateId}
                        className="text-red-500 hover:underline text-xs disabled:opacity-40"
                      >
                        {deleting === r.certificateId ? '…' : 'Delete'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Mobile: compact cards */}
        {!loading && rows.length > 0 && (
          <div className="sm:hidden divide-y divide-gray-100">
            {rows.map((r) => (
              <div key={r.certificateId} className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-navy font-georgia truncate">{r.primaryName || '—'}</p>
                    <p className="text-xs text-gray-500 font-georgia">{r.refSerialNo || '—'}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${TYPE_BADGE[r.certificateType]}`}>
                    {r.certificateType}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="text-xs bg-gray-100 text-navy rounded-full px-2 py-0.5 font-georgia">{fmtDate(r.issuedDate)}</span>
                  <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 font-georgia">Created {fmtDate(r.createdAt)}</span>
                </div>
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={() => navigate(`/certificates/${r.certificateId}/edit`)}
                    className="flex-1 btn-secondary !py-1.5 text-xs"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => navigate(`/certificates/${r.certificateId}/print`)}
                    className="flex-1 btn-secondary !py-1.5 text-xs"
                  >
                    Print
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(r.certificateId)}
                      disabled={deleting === r.certificateId}
                      className="flex-1 btn-danger !py-1.5 text-xs disabled:opacity-40"
                    >
                      {deleting === r.certificateId ? '…' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
